// Moderation uses v2 for reports, staff actions and current roles. The server checks every permission.
import { requestV2 } from './v2-only.ts'

type Sides = typeof import('./moderation-v2.ts')
const request = (use: (loaded: Sides) => Promise<Response>) =>
  requestV2(() => import('./moderation-v2.ts'), use)

export type Reported = { id: string } & Record<string, unknown>

export const reportMedia = (
  token: string,
  imageData: Reported,
  reasons: string[],
) => request((loaded) => loaded.report(token, {}, imageData.id, reasons))

export type FeedAsk = {
  dateRange: string
  limit: number
  page: number
  /** The preceding page's cursor; null for the first page. */
  after: string | null
}

export const reportFeed = (token: string, ask: FeedAsk) =>
  request((loaded) => loaded.feed(token, {}, ask))

/** Which part of the picture panel's grey zone to show; null for all three. */
export type GreyKind = 'refused' | 'review' | 'near'

/**
 * Which grey zone list: public pictures near the age line, or the pictures the check before delivery let through
 * unsure (with today's counts). v2's `source`.
 */
export type GreySource = 'public' | 'delivered_unsure'

export type GreyZoneAsk = {
  /** Left out: the public list. */
  source?: GreySource
  /** Only on the public list: v2 refuses a kind with `delivered_unsure`. */
  kind: GreyKind | null
  limit: number
  /** The preceding page's cursor; null for the first page. */
  after: string | null
}

/** A page of the picture panel's grey zone, for staff: `{ items, next, today }` (moderation-v2.ts). */
export const greyZone = (token: string, ask: GreyZoneAsk) =>
  request((loaded) => loaded.greyZone(token, {}, ask))

export const approveMedia = (token: string, imageId: string) =>
  request((loaded) => loaded.approve(token, {}, imageId))

export const removeAsModerator = (
  token: string,
  body: {
    image_id: string
    reason?: string
    user_id?: string
    user_uid?: string
  },
) =>
  request((loaded) =>
    loaded.remove(token, {}, body.image_id, body.reason ?? null),
  )

export const removeOthers = (
  token: string,
  body: { imageId: string; imageUid: string },
) => request((loaded) => loaded.remove(token, {}, body.imageId, null))

/** No staff buttons when current roles cannot be read; build-time uid lists never grant a role. */
export async function staffRoles(token: string): Promise<string[]> {
  try {
    const response = await request((loaded) => loaded.roles(token, {}))
    if (!response.ok) return []
    const body = await response.json().catch(() => null)
    // v2 answers each role as `{ role, until }` (StaffRole); only its name is kept.
    return Array.isArray(body?.roles)
      ? body.roles
          .map((held: { role?: unknown } | null) => held?.role)
          .filter((role: unknown): role is string => typeof role === 'string')
      : []
  } catch {
    return []
  }
}

export const MODERATING = ['moderator', 'admin']
